import React from "react";
import Modal from "components/ui/Modal";
import DataTablePagination from "components/ui/DataTablePagination";
import { toastError, toastSuccess } from "lib/toast";
import { formatLocaleDateTime, formatLocaleNumber } from "lib/localeFormat";
import { adminService } from "services/adminService";
import type {
  AdminAppleWalletTopup,
  AdminWalletLedgerEntry,
  AdminWalletOverview,
} from "services/adminWalletService";

type Props = {
  open: boolean;
  onClose: () => void;
  studentId: number | null;
  studentName: string;
  isArabic: boolean;
  onUpdated?: () => void;
};

const emptyAdjustForm = {
  direction: "credit" as "credit" | "debit",
  amount: "",
  reason: "",
  payment_method: "cash",
  reference: "",
};

const typeLabels: Record<string, [string, string]> = {
  topup_apple: ["شحن Apple", "Apple top-up"],
  topup_manual: ["شحن يدوي", "Manual top-up"],
  spend_course: ["شراء كورس", "Course spend"],
  refund_clawback: ["استرجاع", "Clawback"],
  admin_adjust: ["تعديل أدمن", "Admin adjust"],
};

function typeLabel(type: string, isArabic: boolean): string {
  const labels = typeLabels[type];
  return labels ? (isArabic ? labels[0] : labels[1]) : type || "—";
}

function money(value: number | null | undefined, currency: string, isArabic: boolean): string {
  if (value === null || value === undefined) return "—";
  return formatLocaleNumber(value, isArabic, { maximumFractionDigits: 2 }) + " " + currency;
}

function formatDate(value: string | null | undefined, isArabic: boolean): string {
  return formatLocaleDateTime(value, isArabic, { dateStyle: "medium", timeStyle: "short" });
}

export default function StudentWalletModal({
  open,
  onClose,
  studentId,
  studentName,
  isArabic,
  onUpdated,
}: Props) {
  const [overview, setOverview] = React.useState<AdminWalletOverview | null>(null);
  const [ledger, setLedger] = React.useState<AdminWalletLedgerEntry[]>([]);
  const [topups, setTopups] = React.useState<AdminAppleWalletTopup[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [range, setRange] = React.useState({ from: 0, to: 0 });
  const [typeFilter, setTypeFilter] = React.useState("");
  const [section, setSection] = React.useState<"ledger" | "apple">("ledger");
  const [adjustOpen, setAdjustOpen] = React.useState(false);
  const [adjustSaving, setAdjustSaving] = React.useState(false);
  const [adjustForm, setAdjustForm] = React.useState(emptyAdjustForm);

  const load = React.useCallback(async () => {
    if (studentId === null) return;
    setLoading(true);
    try {
      const [summary, ledgerResult, topupResult] = await Promise.all([
        adminService.getStudentWallet(studentId),
        adminService.getStudentWalletLedger(studentId, {
          page,
          limit: 15,
          type: typeFilter || undefined,
        }),
        adminService.getStudentAppleTopups(studentId, { page: 1, limit: 50 }),
      ]);
      setOverview(summary);
      setLedger(ledgerResult.data || []);
      setLastPage(Math.max(1, Number(ledgerResult.meta?.last_page) || 1));
      setTotal(Number(ledgerResult.meta?.total) || 0);
      setRange({
        from: Number(ledgerResult.meta?.from) || 0,
        to: Number(ledgerResult.meta?.to) || 0,
      });
      setTopups(topupResult.data || []);
    } catch (error) {
      console.error("Failed to load student wallet:", error);
      toastError(isArabic ? "تعذر تحميل محفظة الطالب." : "Could not load student wallet.");
    } finally {
      setLoading(false);
    }
  }, [studentId, isArabic, page, typeFilter]);

  React.useEffect(() => {
    if (open) void load();
    else {
      setOverview(null);
      setLedger([]);
      setTopups([]);
      setAdjustOpen(false);
      setAdjustForm(emptyAdjustForm);
      setSection("ledger");
      setTypeFilter("");
      setPage(1);
    }
  }, [open, load]);

  React.useEffect(() => {
    if (open) setPage(1);
  }, [open, typeFilter]);

  const submitAdjust = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (studentId === null) return;
    const amount = Number(adjustForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toastError(isArabic ? "أدخل مبلغاً صحيحاً." : "Enter a valid amount.");
      return;
    }
    if (!adjustForm.reason.trim()) {
      toastError(isArabic ? "أدخل سبب التعديل." : "Enter an adjustment reason.");
      return;
    }
    setAdjustSaving(true);
    try {
      const result = await adminService.adjustStudentWallet(studentId, {
        direction: adjustForm.direction,
        amount,
        reason: adjustForm.reason.trim(),
        payment_method: adjustForm.payment_method || null,
        reference: adjustForm.reference.trim() || null,
      });
      if (result?.overview) setOverview(result.overview);
      setAdjustForm(emptyAdjustForm);
      setAdjustOpen(false);
      toastSuccess(isArabic ? "تم تعديل الرصيد بنجاح." : "Wallet adjusted successfully.");
      onUpdated?.();
      await load();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.result?.message;
      toastError(message || (isArabic ? "تعذر تعديل الرصيد." : "Could not adjust wallet."));
    } finally {
      setAdjustSaving(false);
    }
  };

  const currency = overview?.currency || "EGP";
  const cards = overview
    ? [
        [isArabic ? "الرصيد الحالي" : "Current balance", money(overview.balance, currency, isArabic), "text-emerald-600"],
        [isArabic ? "إجمالي الشحن" : "Total credited", money(overview.totals.total_credited, currency, isArabic), "text-blue-600"],
        [isArabic ? "إجمالي الخصم" : "Total debited", money(overview.totals.total_debited, currency, isArabic), "text-orange-600"],
        [isArabic ? "شحن Apple" : "Apple top-ups", money(overview.totals.apple_topups, currency, isArabic), "text-purple-600"],
        [isArabic ? "إنفاق الكورسات" : "Course spend", money(overview.totals.course_spend, currency, isArabic), "text-rose-600"],
        [isArabic ? "تعديلات الأدمن" : "Admin adjust net", money(overview.totals.admin_adjust_net, currency, isArabic), "text-navy-700 dark:text-white"],
        [isArabic ? "شحنات ملغاة" : "Revoked Apple", String(overview.totals.revoked_apple_topups), "text-amber-600"],
      ]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isArabic ? "محفظة الطالب: " + studentName : "Student wallet: " + studentName}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
        >
          {isArabic ? "إغلاق" : "Close"}
        </button>
      }
    >
      {loading && !overview ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {isArabic ? "جارٍ تحميل المحفظة..." : "Loading wallet..."}
        </p>
      ) : (
        <div className="space-y-6" dir={isArabic ? "rtl" : "ltr"}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map(([label, value, color]) => (
              <div key={label} className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
                <p className="text-xs text-gray-500">{label}</p>
                <p className={"mt-1 text-lg font-bold " + color}>{value}</p>
              </div>
            ))}
          </div>

          <section className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold text-navy-700 dark:text-white">
                  {isArabic ? "تعديل الرصيد" : "Adjust balance"}
                </h4>
                <p className="mt-1 text-xs text-gray-500">
                  {isArabic
                    ? "شحن أو خصم يدوي مع تسجيل السبب في السجل."
                    : "Manual credit or debit with a reason in the ledger."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAdjustOpen((value) => !value)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                {adjustOpen
                  ? isArabic
                    ? "إلغاء"
                    : "Cancel"
                  : isArabic
                    ? "تعديل الرصيد"
                    : "Adjust balance"}
              </button>
            </div>
            {adjustOpen ? (
              <form
                onSubmit={submitAdjust}
                className="mt-4 grid gap-3 rounded-lg bg-gray-50 p-4 dark:bg-white/[0.04] sm:grid-cols-2"
              >
                <label className="text-xs text-gray-600 dark:text-gray-300">
                  {isArabic ? "الاتجاه" : "Direction"}
                  <select
                    value={adjustForm.direction}
                    onChange={(event) =>
                      setAdjustForm((form) => ({
                        ...form,
                        direction: event.target.value as "credit" | "debit",
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  >
                    <option value="credit">{isArabic ? "شحن" : "Credit"}</option>
                    <option value="debit">{isArabic ? "خصم" : "Debit"}</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600 dark:text-gray-300">
                  {isArabic ? "المبلغ" : "Amount"}
                  <input
                    required
                    min="0.01"
                    step="0.01"
                    type="number"
                    value={adjustForm.amount}
                    onChange={(event) =>
                      setAdjustForm((form) => ({ ...form, amount: event.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                </label>
                <label className="text-xs text-gray-600 dark:text-gray-300">
                  {isArabic ? "طريقة الدفع" : "Payment method"}
                  <select
                    value={adjustForm.payment_method}
                    onChange={(event) =>
                      setAdjustForm((form) => ({ ...form, payment_method: event.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  >
                    <option value="cash">{isArabic ? "نقدي" : "Cash"}</option>
                    <option value="bank_transfer">{isArabic ? "تحويل بنكي" : "Bank transfer"}</option>
                    <option value="vodafone_cash">Vodafone Cash</option>
                    <option value="instapay">InstaPay</option>
                    <option value="other">{isArabic ? "أخرى" : "Other"}</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600 dark:text-gray-300">
                  {isArabic ? "المرجع (اختياري)" : "Reference (optional)"}
                  <input
                    value={adjustForm.reference}
                    onChange={(event) =>
                      setAdjustForm((form) => ({ ...form, reference: event.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                </label>
                <label className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2">
                  {isArabic ? "السبب" : "Reason"}
                  <textarea
                    required
                    rows={2}
                    value={adjustForm.reason}
                    onChange={(event) =>
                      setAdjustForm((form) => ({ ...form, reason: event.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                </label>
                <div className="flex items-center justify-end sm:col-span-2">
                  <button
                    type="submit"
                    disabled={adjustSaving}
                    className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {adjustSaving
                      ? isArabic
                        ? "جارٍ الحفظ..."
                        : "Saving..."
                      : isArabic
                        ? "حفظ التعديل"
                        : "Save adjustment"}
                  </button>
                </div>
              </form>
            ) : null}
          </section>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSection("ledger")}
              className={
                "rounded-lg px-3 py-1.5 text-sm font-semibold " +
                (section === "ledger"
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white")
              }
            >
              {isArabic ? "سجل الحركات" : "Ledger"}
            </button>
            <button
              type="button"
              onClick={() => setSection("apple")}
              className={
                "rounded-lg px-3 py-1.5 text-sm font-semibold " +
                (section === "apple"
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white")
              }
            >
              {isArabic ? "شحنات Apple" : "Apple top-ups"}
            </button>
          </div>

          {section === "ledger" ? (
            <section>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-sm font-bold text-navy-700 dark:text-white">
                  {isArabic ? "المعاملات (" + total + ")" : "Transactions (" + total + ")"}
                </h4>
                <select
                  value={typeFilter}
                  onChange={(event) => {
                    setTypeFilter(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-xs dark:border-white/10 dark:bg-navy-900 dark:text-white"
                >
                  <option value="">{isArabic ? "كل الأنواع" : "All types"}</option>
                  <option value="topup_apple">{isArabic ? "شحن Apple" : "Apple top-up"}</option>
                  <option value="topup_manual">{isArabic ? "شحن يدوي" : "Manual top-up"}</option>
                  <option value="spend_course">{isArabic ? "شراء كورس" : "Course spend"}</option>
                  <option value="refund_clawback">{isArabic ? "استرجاع" : "Clawback"}</option>
                  <option value="admin_adjust">{isArabic ? "تعديل أدمن" : "Admin adjust"}</option>
                </select>
              </div>
              {ledger.length === 0 ? (
                <p className="mt-3 rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-white/10">
                  {isArabic ? "لا توجد حركات لهذه المحفظة." : "No ledger entries for this wallet."}
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
                  <table className="min-w-[900px] w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-white/5">
                      <tr>
                        <th className="px-3 py-3 text-start">{isArabic ? "التاريخ" : "Date"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "النوع" : "Type"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "المبلغ" : "Amount"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "الرصيد بعد" : "Balance after"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "التفاصيل" : "Details"}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                      {ledger.map((entry) => {
                        const reason =
                          typeof entry.meta?.reason === "string" ? entry.meta.reason : null;
                        const paymentMethod =
                          typeof entry.meta?.payment_method === "string"
                            ? entry.meta.payment_method
                            : null;
                        const paymentReference =
                          typeof entry.meta?.payment_reference === "string"
                            ? entry.meta.payment_reference
                            : typeof entry.meta?.reference === "string"
                              ? entry.meta.reference
                              : null;
                        const details = [
                          reason,
                          paymentMethod
                            ? paymentMethod === "vodafone_cash"
                              ? isArabic
                                ? "فودافون كاش"
                                : "Vodafone Cash"
                              : paymentMethod === "instapay"
                                ? isArabic
                                  ? "إنستا باي"
                                  : "InstaPay"
                                : paymentMethod
                            : null,
                          paymentReference,
                          entry.reference_id,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <tr key={entry.id}>
                            <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                              {formatDate(entry.created_at, isArabic)}
                            </td>
                            <td className="px-3 py-3">
                              <span className="rounded-full bg-blue-100 px-2 py-1 text-[11px] font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-200">
                                {typeLabel(entry.type, isArabic)}
                              </span>
                            </td>
                            <td
                              className={
                                "whitespace-nowrap px-3 py-3 font-semibold " +
                                (entry.amount >= 0 ? "text-emerald-600" : "text-rose-600")
                              }
                            >
                              {money(entry.amount, currency, isArabic)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 font-semibold text-navy-700 dark:text-white">
                              {money(entry.balance_after, currency, isArabic)}
                            </td>
                            <td className="max-w-[240px] px-3 py-3 text-xs text-gray-500">
                              {details || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {total > 0 ? (
                <DataTablePagination
                  isArabic={isArabic}
                  page={page}
                  totalPages={lastPage}
                  fromItem={range.from}
                  toItem={range.to}
                  totalItems={total}
                  onPageChange={setPage}
                  className="mt-2"
                />
              ) : null}
            </section>
          ) : (
            <section>
              <h4 className="text-sm font-bold text-navy-700 dark:text-white">
                {isArabic ? "شحنات Apple (" + topups.length + ")" : "Apple top-ups (" + topups.length + ")"}
              </h4>
              {topups.length === 0 ? (
                <p className="mt-3 rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-white/10">
                  {isArabic ? "لا توجد شحنات Apple لهذا الطالب." : "No Apple top-ups for this student."}
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
                  <table className="min-w-[900px] w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-white/5">
                      <tr>
                        <th className="px-3 py-3 text-start">{isArabic ? "التاريخ" : "Date"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "المنتج" : "Product"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "المبلغ" : "Amount"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "البيئة" : "Environment"}</th>
                        <th className="px-3 py-3 text-start">{isArabic ? "الحالة" : "Status"}</th>
                        <th className="px-3 py-3 text-start">Txn</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                      {topups.map((topup) => (
                        <tr key={topup.id}>
                          <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                            {formatDate(topup.purchased_at || topup.created_at, isArabic)}
                          </td>
                          <td className="px-3 py-3 text-xs">{topup.product_id}</td>
                          <td className="whitespace-nowrap px-3 py-3 font-semibold text-navy-700 dark:text-white">
                            {money(topup.credit_amount, currency, isArabic)}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-500">{topup.environment || "—"}</td>
                          <td className="px-3 py-3">
                            {topup.is_revoked ? (
                              <span className="rounded-full bg-rose-100 px-2 py-1 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/10 dark:text-rose-200">
                                {(isArabic ? "ملغى" : "Revoked") +
                                  (topup.revoke_reason ? " · " + topup.revoke_reason : "")}
                              </span>
                            ) : (
                              <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                                {isArabic ? "نشط" : "Active"}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 font-mono text-[11px] text-gray-500">
                            {topup.transaction_id}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
